import { useTranslation } from 'react-i18next';
import { DataTableViewOptions } from '@/components/data-table/data-table-view-options';
import { ErrorBoundarySuspense } from '@/components/error-boundary-suspense';
import { LotSizingSupplierTable } from '@/features/lot-sizing/component/lot-sizing-supplier-table';
import { hasRelatedPurchaseOrders } from '@/features/lot-sizing/has-related-purchase-orders';
import { getLotSizingLinesBySupplierInfiniteQueryKey } from '@/features/lot-sizing/hooks/use-lot-sizing-lines-by-supplier';
import { useLotSizingTable } from '@/features/lot-sizing/hooks/use-lot-sizing-table';
import { LotSizingTableAddLineDialog } from '@/features/lot-sizing/lot-sizing-table-add-line-dialog';
import { LotSizingSupplierTableLineSkeleton } from '@/features/lot-sizing/skeleton/lot-sizing-supplier-table-line-skeleton';
import { useGetLotSizing } from '@/services/apis/queries/lot-sizing/use-get-lot-sizing';

type LotSizingRefsTableProps = {
  lotSizingId: string;
};

export function LotSizingRefsTable({ lotSizingId }: LotSizingRefsTableProps) {
  const { t } = useTranslation('lotSizing');

  const { data: lotSizing } = useGetLotSizing(lotSizingId);
  const suppliers = lotSizing.suppliers ?? [];
  const lotSizingHasRelatedPurchaseOrders = hasRelatedPurchaseOrders(lotSizing);

  const { table } = useLotSizingTable();

  return (
    <div className='mb-10 flex flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <h3 className='font-semibold text-lg'>{t('refsListTitle')}</h3>
        <div className='flex items-center gap-2'>
          <LotSizingTableAddLineDialog lotSizingId={lotSizingId} />
          <DataTableViewOptions align='end' table={table} />
        </div>
      </div>
      <div className='flex flex-col gap-4'>
        {suppliers.map((supplier) => {
          const queryKey = getLotSizingLinesBySupplierInfiniteQueryKey(
            lotSizingId,
            String(supplier.id),
          );
          return (
            <ErrorBoundarySuspense
              errorFallbackWrapper={
                <div className='rounded-md border bg-background p-4' />
              }
              key={supplier.id}
              loadingFallback={<LotSizingSupplierTableLineSkeleton />}
              message={t('unableToLoad')}
              queryKey={queryKey}
            >
              <LotSizingSupplierTable
                defaultOpen={suppliers.length === 1}
                hasRelatedPurchaseOrders={lotSizingHasRelatedPurchaseOrders}
                lotSizingId={lotSizingId}
                supplier={supplier}
                table={table}
              />
            </ErrorBoundarySuspense>
          );
        })}
      </div>
    </div>
  );
}
